import React, { useState } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import { RISK_LEVELS, normalizeRiskLevel } from '../../utils/riskLevel';

const availableSources = [
  { id: 'federal_register', name: 'Federal Register', description: 'US Federal regulations' },
  { id: 'nist', name: 'NIST', description: 'NIST cybersecurity frameworks' },
  { id: 'iso', name: 'ISO', description: 'International standards' },
  { id: 'country_regulatory', name: 'Country Regulatory', description: 'Various country regulations' },
  { id: 'eur_lex', name: 'EUR-Lex', description: 'European Union law' }
];

const categories = [
  'cybersecurity', 'data_protection', 'risk_management', 'governance',
  'incident_response', 'business_continuity', 'disaster_recovery',
  'supply_chain_cybersecurity', 'third_party_risk',
  'ai_security', 'machine_learning_security', 'cloud_security',
  'iot_security', 'devsecops', 'ci_cd_security',
  'financial_security', 'healthcare_security', 'critical_infrastructure',
  'government_security', 'defense_security',
  'privacy_compliance', 'regulatory_compliance', 'audit_compliance',
  'network_security', 'application_security', 'endpoint_security',
  'identity_access_management', 'cryptography', 'threat_intelligence'
];

const frameworkTypes = [
  'regulation', 'standard', 'framework', 'guideline',
  'best_practice', 'law', 'directive'
];



const countries = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia',
  'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
  'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
  'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde',
  'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
  'East Timor', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
  'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany',
  'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti',
  'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
  'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan',
  'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania',
  'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
  'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro',
  'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand',
  'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan',
  'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
  'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles',
  'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa',
  'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland',
  'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Togo', 'Tonga', 'Trinidad and Tobago',
  'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates',
  'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela',
  'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

const jurisdictions = [
  'Global',
  'European Union',
  ...countries
];

const getTypeDisplayName = (type) => {
  const typeMap = {
    'regulation': 'Regulation',
    'standard': 'Standard',
    'framework': 'Framework',
    'guideline': 'Guideline',
    'best_practice': 'Best Practice',
    'law': 'Law',
    'directive': 'Directive'
  };
  return typeMap[type] || type;
};

const getCategoryDisplayName = (category) => {
  const categoryMap = {
    'cybersecurity': 'Cybersecurity',
    'data_protection': 'Data Protection',
    'risk_management': 'Risk Management',
    'governance': 'Governance',
    'incident_response': 'Incident Response',
    'business_continuity': 'Business Continuity',
    'disaster_recovery': 'Disaster Recovery',
    'supply_chain_cybersecurity': 'Supply Chain Cybersecurity',
    'third_party_risk': 'Third Party Risk',
    'ai_security': 'AI Security',
    'machine_learning_security': 'Machine Learning Security',
    'cloud_security': 'Cloud Security',
    'iot_security': 'IoT Security',
    'devsecops': 'DevSecOps',
    'ci_cd_security': 'CI/CD Security',
    'financial_security': 'Financial Security',
    'healthcare_security': 'Healthcare Security',
    'critical_infrastructure': 'Critical Infrastructure',
    'government_security': 'Government Security',
    'defense_security': 'Defense Security',
    'privacy_compliance': 'Privacy Compliance',
    'regulatory_compliance': 'Regulatory Compliance',
    'audit_compliance': 'Audit Compliance',
    'network_security': 'Network Security',
    'application_security': 'Application Security',
    'endpoint_security': 'Endpoint Security',
    'identity_access_management': 'Identity & Access Management',
    'cryptography': 'Cryptography',
    'threat_intelligence': 'Threat Intelligence'
  };
  return categoryMap[category] || category;
};

function DiscoveryTab({
  frameworks,
  isDiscovering,
  setIsDiscovering,
  discoveryResults,
  setDiscoveryResults,
  selectedSources,
  setSelectedSources,
  searchTerms,
  setSearchTerms,
  customUrls,
  setCustomUrls,
  selectedForImport,
  setSelectedForImport,
  discoveryResultsRef,
  onSuccess,
  onError,
  onRefreshFrameworks
}) {
  const [isAddingFramework, setIsAddingFramework] = useState(false);
  const [editingFrameworks, setEditingFrameworks] = useState({});
  const [editedFrameworks, setEditedFrameworks] = useState({});

  const handleDiscoverFrameworks = async () => {
    const hasCustomUrls = customUrls && customUrls.trim().split('\n').some(url => url.trim());
    if (selectedSources.length === 0 && !hasCustomUrls) {
      onError('Please select at least one source or provide custom URLs for discovery.');
      return;
    }

    setIsDiscovering(true);
    try {
      const payload = {
        sources: selectedSources,
        search_terms: searchTerms,
        custom_urls: customUrls ? customUrls.split('\n').map(url => url.trim()).filter(url => url) : [],
        max_results_per_source: 20
      };

      const response = await axios.post(`${Config.APIBaseURL}/grc/frameworks/discover`, payload, {
        headers: {
          'access_token': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      setDiscoveryResults(response.data);
      setSelectedForImport([]);

      setTimeout(() => {
        if (discoveryResultsRef.current) {
          discoveryResultsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);

    } catch (error) {
      console.error('Error discovering frameworks:', error);
      if (error.response?.data?.detail) {
        onError(`Discovery failed: ${error.response.data.detail}`);
      } else {
        onError('Discovery failed. Please check your inputs and try again.');
      }
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleAddFrameworks = async (frameworksToAdd) => {
    setIsAddingFramework(true);
    try {
      const payload = {
        frameworks: frameworksToAdd,
        overwrite_existing: false
      };

      const response = await axios.post(`${Config.APIBaseURL}/grc/frameworks/add`, payload, {
        headers: {
          'access_token': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      await onRefreshFrameworks();

      setSelectedForImport([]);
      onSuccess(`Successfully added ${response.data.summary.successfully_added} frameworks!`);

    } catch (error) {
      console.error('Error adding frameworks:', error);
      onError('Error adding frameworks. Please try again.');
    } finally {
      setIsAddingFramework(false);
    }
  };

  const startEditingFramework = (index) => {
    const framework = discoveryResults.frameworks[index];
    setEditingFrameworks(prev => ({ ...prev, [index]: true }));
    setEditedFrameworks(prev => ({
      ...prev,
      [index]: { ...framework }
    }));
  };

  const saveFrameworkEdit = (index) => {
    const editedFramework = editedFrameworks[index];
    if (editedFramework) {
      const updatedFrameworks = [...discoveryResults.frameworks];
      updatedFrameworks[index] = editedFramework;
      setDiscoveryResults(prev => ({
        ...prev,
        frameworks: updatedFrameworks
      }));

      setSelectedForImport(prev =>
        prev.map(fw =>
          fw.short_name === discoveryResults.frameworks[index].short_name
            ? editedFramework
            : fw
        )
      );
    }

    setEditingFrameworks(prev => ({ ...prev, [index]: false }));
    setEditedFrameworks(prev => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
  };

  const cancelFrameworkEdit = (index) => {
    setEditingFrameworks(prev => ({ ...prev, [index]: false }));
    setEditedFrameworks(prev => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
  };

  const updateEditedFramework = (index, field, value) => {
    setEditedFrameworks(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value
      }
    }));
  };

  return (
    <div className="space-y-6">
      {/* Discovery Configuration */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6">
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-6">
          Discover New Frameworks
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sources */}
          <div>
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-4">
              Select Sources
            </h3>
            <div className="space-y-3">
              {availableSources.map(source => (
                <label key={source.id} className="flex items-start space-x-3 p-3 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedSources.includes(source.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedSources([...selectedSources, source.id]);
                      } else {
                        setSelectedSources(selectedSources.filter(s => s !== source.id));
                      }
                    }}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-0.5"
                  />
                  <div>
                    <div className="font-medium text-slate-700 dark:text-slate-300">{source.name}</div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">{source.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Search Configuration */}
          <div>
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-4">
              Search Configuration
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Search Terms
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {searchTerms.map((term, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200">
                      {term}
                      <button
                        onClick={() => setSearchTerms(searchTerms.filter((_, i) => i !== index))}
                        className="ml-2 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Add search term and press Enter"
                  className="w-full text-sm px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      setSearchTerms([...searchTerms, e.target.value.trim()]);
                      e.target.value = '';
                    }
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Custom URLs (one per line)
                </label>
                <textarea
                  value={customUrls}
                  onChange={(e) => setCustomUrls(e.target.value)}
                  placeholder={"https://example.com/regulations\nhttps://another-source.gov/frameworks"}
                  rows={4}
                  className="w-full text-sm px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <button
                onClick={handleDiscoverFrameworks}
                disabled={isDiscovering || (selectedSources.length === 0 && !customUrls.trim())}
                className="w-full inline-flex items-center justify-center text-sm px-4 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDiscovering ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Discovering Frameworks...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Start Discovery
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Discovery Results */}
      {discoveryResults && (
        <div
          ref={discoveryResultsRef}
          className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
                Discovery Results
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                Found {discoveryResults.discovery_summary.total_frameworks_found} frameworks
                ({discoveryResults.discovery_summary.new_frameworks} new, {discoveryResults.discovery_summary.existing_frameworks} existing)
              </p>
            </div>
            {selectedForImport.length > 0 && (
              <button
                onClick={() => handleAddFrameworks(selectedForImport)}
                disabled={isAddingFramework}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50"
              >
                {isAddingFramework ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Adding...
                  </>
                ) : (
                  `Add ${selectedForImport.length} Selected`
                )}
              </button>
            )}
          </div>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {discoveryResults.frameworks.map((framework, index) => (
              <div
                key={index}
                className={`border rounded-lg p-4 ${
                  framework.already_exists
                    ? 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50'
                    : 'border-slate-200 dark:border-slate-600 hover:border-indigo-300 dark:hover:border-indigo-500'
                }`}
              >
                {editingFrameworks[index] ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Framework Name *
                        </label>
                        <input
                          type="text"
                          value={editedFrameworks[index]?.name || ''}
                          onChange={(e) => updateEditedFramework(index, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Short Name/Acronym *
                        </label>
                        <input
                          type="text"
                          value={editedFrameworks[index]?.short_name || ''}
                          onChange={(e) => updateEditedFramework(index, 'short_name', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Type
                        </label>
                        <select
                          value={editedFrameworks[index]?.type || ''}
                          onChange={(e) => updateEditedFramework(index, 'type', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">Select type</option>
                          {frameworkTypes.map(type => (
                            <option key={type} value={type}>{getTypeDisplayName(type)}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Category
                        </label>
                        <select
                          value={editedFrameworks[index]?.category || ''}
                          onChange={(e) => updateEditedFramework(index, 'category', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">Select category</option>
                          {categories.map(category => (
                            <option key={category} value={category}>{getCategoryDisplayName(category)}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Jurisdiction
                        </label>
                        <select
                          value={editedFrameworks[index]?.jurisdiction || ''}
                          onChange={(e) => updateEditedFramework(index, 'jurisdiction', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">Select jurisdiction</option>
                          {jurisdictions.map(jurisdiction => (
                            <option key={jurisdiction} value={jurisdiction}>{jurisdiction}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Risk Level
                        </label>
                        <select
                          value={normalizeRiskLevel(editedFrameworks[index]?.risk_level) || 'medium'}
                          onChange={(e) => updateEditedFramework(index, 'risk_level', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          {RISK_LEVELS.map(level => (
                            <option key={level.value} value={level.value}>{level.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Description
                      </label>
                      <textarea
                        value={editedFrameworks[index]?.description || ''}
                        onChange={(e) => updateEditedFramework(index, 'description', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => cancelFrameworkEdit(index)}
                        className="px-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => saveFrameworkEdit(index)}
                        className="px-3 py-1.5 text-sm bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      {!framework.already_exists && (
                        <input
                          type="checkbox"
                          checked={selectedForImport.some(fw => fw.short_name === framework.short_name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedForImport([...selectedForImport, framework]);
                            } else {
                              setSelectedForImport(selectedForImport.filter(fw => fw.short_name !== framework.short_name));
                            }
                          }}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-1"
                        />
                      )}
                      <div>
                        <h4 className="font-semibold text-slate-800 dark:text-slate-100">
                          {framework.name}
                        </h4>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {framework.short_name} • {framework.source} • {getCategoryDisplayName(framework.category)}
                        </p>
                        {framework.description && (
                          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                            {framework.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {!framework.already_exists && (
                        <button
                          onClick={() => startEditingFramework(index)}
                          className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                          title="Edit framework"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                      {framework.already_exists && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200">
                          Already exists
                        </span>
                      )}
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                        {framework.source}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default DiscoveryTab;
